import type { AircraftGeomParms, AircraftParm } from "../../api/aircraft";

export type X57VspFidelity = "simple" | "simplified-crm-v4.4.1" | "detailed-crm-v4.4.1";
export type X57LengthUnit = "ft" | "in";

export interface X57ParameterSource {
  fidelity: X57VspFidelity;
}

export interface X57EditableParameter extends AircraftParm {
  unit: X57LengthUnit | "deg" | "ratio";
  uiMin: number;
  uiMax: number;
}

type ParameterUi = { kind: "length" | "angle" | "ratio"; min: number; max: number };

const PARAMETER_UI: Readonly<Record<string, ParameterUi>> = {
  Span: { kind: "length", min: 8, max: 24 },
  Root_Chord: { kind: "length", min: 1.2, max: 4 },
  Tip_Chord: { kind: "length", min: 0.7, max: 3 },
  Sweep: { kind: "angle", min: -10, max: 25 },
  Sweep_Location: { kind: "ratio", min: 0, max: 1 },
  Twist: { kind: "angle", min: -10, max: 5 },
};

export function normalizeX57GeomName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function isX57MainWingName(name: string): boolean {
  return ["mainwing", "cleanwing"].includes(normalizeX57GeomName(name));
}

export function x57LengthUnit(source: X57ParameterSource): X57LengthUnit {
  return source.fidelity === "simple" ? "ft" : "in";
}

function parameterUnit(ui: ParameterUi, source: X57ParameterSource): X57EditableParameter["unit"] {
  if (ui.kind === "length") return x57LengthUnit(source);
  return ui.kind === "angle" ? "deg" : "ratio";
}

/** Select only the exact editable lifting-section values supplied by VSP. */
export function selectX57MainWingParameters(
  geoms: AircraftGeomParms[],
  source: X57ParameterSource,
): X57EditableParameter[] {
  const mainWing = geoms.find((geom) => isX57MainWingName(geom.name));
  if (!mainWing) return [];
  const lengthFactor = x57LengthUnit(source) === "in" ? 12 : 1;
  return mainWing.parms
    .filter((parm) => parm.group === "XSec_1" && PARAMETER_UI[parm.name] !== undefined)
    .map((parm) => {
      const ui = PARAMETER_UI[parm.name]!;
      const factor = ui.kind === "length" ? lengthFactor : 1;
      const boundedMin = Math.max(ui.min * factor, parm.lo);
      const boundedMax = Math.min(ui.max * factor, parm.hi);
      return {
        ...parm,
        unit: parameterUnit(ui, source),
        uiMin: boundedMin < boundedMax ? boundedMin : parm.lo,
        uiMax: boundedMin < boundedMax ? boundedMax : parm.hi,
      };
    });
}
